import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import brandLogoDark from "../utils/brandLogo";
import {
  INSTALL_AVAILABLE_EVENT,
  INSTALLED_EVENT,
  dismissInstallPrompt,
  getDeferredInstallPrompt,
  isDesktopDevice,
  isInstallPromptDismissed,
  isStandaloneDisplay,
  promptPwaInstall,
  shouldHideInstallOnPath,
} from "../utils/pwaInstall";
import "./css/PwaInstallBanner.css";

const SHOW_DELAY_MS = 2500;

export default function PwaInstallBanner() {
  const { t } = useTranslation("mobile");
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(() => getDeferredInstallPrompt());
  const [visible, setVisible] = useState(false);
  const [delayElapsed, setDelayElapsed] = useState(false);

  const alreadyInstalled = isStandaloneDisplay();
  const desktopOnly = isDesktopDevice();
  const dismissed = isInstallPromptDismissed();
  const hiddenOnPath = shouldHideInstallOnPath(location.pathname);

  useEffect(() => {
    if (alreadyInstalled) return undefined;
    const timer = window.setTimeout(() => setDelayElapsed(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [alreadyInstalled]);

  useEffect(() => {
    const onAvailable = () => setDeferredPrompt(getDeferredInstallPrompt());
    const onInstalled = () => {
      setDeferredPrompt(null);
      setVisible(false);
    };
    window.addEventListener(INSTALL_AVAILABLE_EVENT, onAvailable);
    window.addEventListener(INSTALLED_EVENT, onInstalled);
    return () => {
      window.removeEventListener(INSTALL_AVAILABLE_EVENT, onAvailable);
      window.removeEventListener(INSTALLED_EVENT, onInstalled);
    };
  }, []);

  useEffect(() => {
    if (alreadyInstalled || dismissed || hiddenOnPath || !delayElapsed || !desktopOnly) {
      setVisible(false);
      return;
    }
    setVisible(Boolean(deferredPrompt));
  }, [alreadyInstalled, deferredPrompt, delayElapsed, dismissed, desktopOnly, hiddenOnPath]);

  const handleDismiss = useCallback(() => {
    dismissInstallPrompt();
    setVisible(false);
  }, []);

  const handleInstall = useCallback(async () => {
    try {
      await promptPwaInstall();
    } finally {
      setDeferredPrompt(null);
      setVisible(false);
    }
  }, []);

  if (!visible) return null;

  const title = t("pwa_install_title", { defaultValue: "Install MFG Invest" });
  const message = t("pwa_install_message", {
    defaultValue: "Add MFG Invest to your home screen for quicker access.",
  });

  return (
    <div className="pwa-install-banner" role="dialog" aria-labelledby="pwa-install-title" aria-describedby="pwa-install-message">
      <img src={brandLogoDark} alt="" className="pwa-install-banner-logo" />
      <div className="pwa-install-banner-copy">
        <h2 id="pwa-install-title" className="pwa-install-banner-title">
          {title}
        </h2>
        <p id="pwa-install-message" className="pwa-install-banner-message">
          {message}
        </p>
      </div>
      <div className="pwa-install-banner-actions">
        {deferredPrompt ? (
          <button type="button" className="pwa-install-banner-install" onClick={handleInstall}>
            {t("pwa_install_button", { defaultValue: "Install" })}
          </button>
        ) : null}
        <button type="button" className="pwa-install-banner-dismiss" onClick={handleDismiss}>
          {t("pwa_install_dismiss", { defaultValue: "Not now" })}
        </button>
      </div>
    </div>
  );
}
